<template>
    <docu-layout title="Terms and Conditions">
        <template #default>
            <div class="terms-container">
                <h1 class="terms-title">Terms of Service</h1>
                <p class="intro-text">
                    The {{ Ajility.DBAName }} website (the "Site") is comprised of various web pages operated by
                    {{ Ajility.ParentCompany }}, doing business as {{ Ajility.DBAName }}. By accessing or using the
                    Site, you agree to the terms, conditions, and notices outlined in this document (the "Terms"). Your
                    use of <a :href="`${Ajility.BaseUrl}`" class="link">{{ Ajility.BaseUrl }}</a> constitutes your
                    agreement to all such Terms. Please read these terms carefully and keep a copy for your reference.
                </p>
                <p class="intro-text">{{ Ajility.DBAName }} is an online document storage and management service.</p>

                <section>
                    <h2>Billing & Subscriptions</h2>
                    <p>
                        {{ Ajility.DBAName }} offers subscription plans for its services. By signing up for a
                        subscription, you agree to the terms and rates associated with the plan you selected. Billing
                        and subscription terms are outlined below:
                    </p>
                    <h3>Subscription Plans and Charges</h3>
                    <ul>
                        <li>
                            <strong>Units of Storage:</strong> Storage is billed in units, where one unit equals
                            {{ convertToGB(SubscriptionMeters.full.usage.metadata.one_unit_is_equal_to) }} GB of total
                            storage.
                        </li>
                        <li>
                            <strong>Plan Rates:</strong> You will be charged according to the rate associated with the
                            subscription plan you accepted at the time of enrollment. These rates may vary depending on
                            the plan and any applicable promotions or discounts at the time.
                        </li>
                    </ul>
                    <h3>Billing Terms</h3>
                    <ul>
                        <li>
                            <strong>Subscription Fees:</strong> You agree to pay the subscription fee associated with
                            your selected plan. The fee is recurring and will be charged based on the billing cycle
                            specified in your plan (e.g., monthly or annually).
                        </li>
                        <li>
                            <strong>Rate Adjustments:</strong> {{ Ajility.DBAName }} reserves the right to modify rates
                            and fees for future billing cycles. You will be notified of any changes in rates prior to
                            the renewal of your subscription.
                        </li>
                        <li>
                            <strong>Payment Methods:</strong> Subscription fees must be paid using a valid payment
                            method on file. It is your responsibility to ensure payment details are up to date.
                        </li>
                    </ul>
                    <h3>User Agreement on Charges</h3>
                    <ul>
                        <li>
                            By subscribing, you agree to pay the rate associated with the subscription plan you accepted
                            at the time of enrollment.
                        </li>
                        <li>
                            The applicable rate remains in effect for the duration of your subscription unless otherwise
                            specified or until the plan is modified, renewed, or canceled.
                        </li>
                    </ul>
                    <h3>Cancellation and Refunds</h3>
                    <ul>
                        <li>
                            <strong>Cancellation:</strong> You may cancel your subscription at any time. Cancellation
                            will take effect at the end of the current billing period.
                        </li>
                        <li>
                            <strong>Data Retention:</strong> After cancellation or unsubscription, you have 30 days to
                            re-enroll in a subscription plan before your data is permanently deleted. Once deleted, the
                            data cannot be retrieved under any circumstances.
                        </li>
                        <li>
                            <strong>Refund Policy:</strong> Fees already paid for the current billing period are
                            non-refundable unless required by applicable laws.
                        </li>
                    </ul>
                    <h3>Default and Termination of Subscription</h3>
                    <ul>
                        <li>
                            If a payment fails or your subscription is terminated due to non-payment, access to services
                            may be restricted until outstanding amounts are settled.
                        </li>
                        <li>
                            {{ Ajility.DBAName }} reserves the right to suspend or cancel subscriptions for violations
                            of these Terms or misuse of the platform.
                        </li>
                    </ul>
                    <p>By subscribing to a plan, you acknowledge and agree to these billing and subscription terms.</p>
                </section>

                <section>
                    <h2>Disclaimer of Liability for Data Loss or Compromise</h2>
                    <p>
                        {{ Ajility.DBAName }} strives to provide secure and reliable document storage. However,
                        {{ Ajility.ParentCompany }} takes no responsibility or liability for any data that is lost,
                        corrupted, or otherwise compromised, whether due to system failures, unauthorized access, user
                        error, or any other reason. By using the Site, you acknowledge and accept that:
                    </p>
                    <ul>
                        <li>
                            You are solely responsible for maintaining independent backups of any data uploaded to the
                            Site.
                        </li>
                        <li>
                            {{ Ajility.DBAName }} shall not be liable for any loss or damage arising from the
                            unavailability, loss, or compromise of your data.
                        </li>
                        <li>
                            {{ Ajility.DBAName }} provides its services "as is" and "as available" without any
                            warranties, express or implied, regarding the security, reliability, or accuracy of your
                            data storage.
                        </li>
                    </ul>
                </section>

                <section>
                    <h2>Prohibited Content and Usage</h2>
                    <p>
                        Users are strictly prohibited from uploading, storing, or sharing any content that is illegal,
                        harmful, or violates the rights of others. This includes, but is not limited to:
                    </p>
                    <ul>
                        <li>Pornographic, obscene, or indecent material.</li>
                        <li>Content that infringes on intellectual property rights.</li>
                        <li>Material that promotes hate, violence, or unlawful activities.</li>
                    </ul>
                    <p>
                        {{ Ajility.DBAName }} may take appropriate action to ensure compliance with applicable laws and
                        the integrity of the platform. If prohibited or unlawful content is identified,
                        {{ Ajility.DBAName }} reserves the right to remove such material at its sole discretion.
                        Additionally, we may report any suspected illegal activity to the appropriate authorities, as
                        required by law.
                    </p>
                    <p>
                        {{ Ajility.DBAName }} does not actively monitor user-uploaded content. However, we reserve the
                        right to review content on our platform under specific circumstances, such as when alerted to
                        potential violations of these Terms. Any review will be conducted in a manner that respects user
                        privacy and complies with applicable laws.
                    </p>
                </section>

                <section>
                    <h2>Electronic Communications</h2>
                    <p>
                        By registering for an account on {{ Ajility.DBAName }} or sending emails to
                        {{ Ajility.DBAName }}, you consent to receive electronic communications. You agree that all
                        agreements, notices, disclosures, and other communications we provide electronically satisfy any
                        legal requirement that such communications be in writing.
                    </p>
                </section>

                <section>
                    <h2>Account Verification via SMS</h2>
                    <p class="mt-2">
                        By creating an account, you agree to receive a one-time SMS message from us for verification
                        purposes. Message frequency is limited to account-related events (e.g., registration, login
                        verification).
                    </p>
                    <p class="mt-2">
                        The message will contain a code formatted as: “Your verification code is: [code]”.
                    </p>
                    <p class="mt-2">
                        Message and data rates may apply. You can reply HELP for assistance or STOP to opt out of
                        further messages. Opting out may impact your ability to access your account.
                    </p>
                </section>

                <section>
                    <h2>Electronic Signatures</h2>
                    <p>
                        By using {{ Ajility.DBAName }}, you agree to the use of electronic signatures and consent to
                        electronically sign documents, agreements, and other communications provided through the
                        platform. This agreement complies with the
                        <strong>Electronic Signatures in Global and National Commerce Act (E-SIGN Act)</strong> and
                        ensures that electronic signatures carry the same legal standing as traditional handwritten
                        signatures.
                    </p>
                    <h3>Your Consent to Use Electronic Signatures</h3>
                    <ul>
                        <li>
                            <strong>Acknowledgment:</strong> You acknowledge and agree that your electronic signature is
                            legally binding and enforceable under applicable laws.
                        </li>
                        <li>
                            <strong>Scope of Consent:</strong> Your consent to use electronic signatures applies to all
                            transactions conducted through {{ Ajility.DBAName }}, including but not limited to
                            agreements, contracts, and notifications.
                        </li>
                        <li>
                            <strong>Right to Withdraw Consent:</strong> You have the right to withdraw your consent to
                            use electronic signatures. However, doing so may limit your ability to use certain features
                            of the platform. To withdraw your consent, please contact us.
                        </li>
                        <li>
                            <strong>Access to Copies:</strong> You have the right to receive copies of any
                            electronically signed documents. Copies may be accessed through your
                            {{ Ajility.DBAName }} account.
                        </li>
                    </ul>
                    <h3>Hardware and Software Requirements</h3>
                    <p>To access and use electronic signatures, you must have:</p>
                    <ul>
                        <li>A device capable of accessing the internet.</li>
                        <li>A compatible web browser (e.g., Chrome, Firefox, Safari).</li>
                        <li>Software capable of opening PDFs or other standard document formats.</li>
                    </ul>
                </section>

                <section>
                    <h2>Your Account</h2>
                    <p>
                        If you create an account on the Site, you are responsible for maintaining the confidentiality of
                        your account credentials and for restricting access to your devices. You agree to accept
                        responsibility for all activities occurring under your account. You may not transfer your
                        account to any other person or entity without prior written consent from {{ Ajility.DBAName }}.
                        {{ Ajility.DBAName }} reserves the right to suspend or terminate accounts at its discretion.
                    </p>
                </section>

                <section>
                    <h2>Right to Terminate Access</h2>
                    <p>
                        {{ Ajility.DBAName }} reserves the right to terminate your access to the Site and its services,
                        with or without prior communication, if we believe such action is necessary to:
                    </p>
                    <ul>
                        <li>Protect the integrity and security of the platform.</li>
                        <li>Comply with legal obligations.</li>
                        <li>
                            Prevent misuse of our services, including but not limited to the uploading of prohibited
                            content or engaging in harmful activities.
                        </li>
                    </ul>
                </section>

                <section>
                    <h2>Inspection of Content</h2>
                    <p>
                        {{ Ajility.DBAName }} values user privacy and employs robust security measures to safeguard your
                        data. However, we reserve the right to review content stored on our platform if we have a
                        reasonable belief that it contains illegal material or violates these Terms. This inspection
                        will only be conducted:
                    </p>
                    <ul>
                        <li>
                            To comply with legal obligations, including responding to valid subpoenas or legal requests.
                        </li>
                        <li>
                            When required to address suspected violations of these Terms, such as the upload of illegal
                            content.
                        </li>
                    </ul>
                    <p>
                        {{ Ajility.DBAName }} will limit such reviews to the minimum extent necessary and will act in
                        accordance with all applicable data protection and privacy laws.
                    </p>
                </section>

                <section>
                    <h2>Children Under Thirteen</h2>
                    <p>
                        {{ Ajility.DBAName }} does not knowingly collect personal information from children under the
                        age of thirteen. If you are under 18, you may use
                        <a :href="`${Ajility.BaseUrl}`" class="link">{{ Ajility.BaseUrl }}</a> only with the involvement
                        of a parent or guardian.
                    </p>
                </section>

                <section>
                    <h2>Links to Third-Party Sites/Third-Party Services</h2>
                    <p>
                        {{ Ajility.DBAName }} may provide links to third-party websites or services.
                        {{ Ajility.DBAName }} is not responsible for the contents or reliability of any linked sites and
                        does not endorse the views expressed within them. Your use of third-party sites is subject to
                        the terms and conditions of those sites.
                    </p>
                </section>

                <section>
                    <h2>No Unlawful or Prohibited Use/Intellectual Property</h2>
                    <p>
                        You are granted a limited, non-exclusive, revocable license to use
                        <a :href="`${Ajility.BaseUrl}`" class="link">{{ Ajility.BaseUrl }}</a> strictly in accordance
                        with these Terms. You agree not to use the Site in any manner that could damage, disable,
                        overburden, or impair the Site or interfere with any other party's use. Unauthorized use of
                        {{ Ajility.DBAName }}\u2019s intellectual property, including reverse engineering, copying, or
                        distribution of Site content, is strictly prohibited.
                    </p>
                </section>

                <section>
                    <h2>Indemnification</h2>
                    <p>
                        You agree to indemnify and hold harmless {{ Ajility.ParentCompany }}, its officers, directors,
                        employees, agents, and affiliates from any claims, damages, or expenses arising from:
                    </p>
                    <ul>
                        <li>Your use of the Site or services;</li>
                        <li>Breach of these Terms;</li>
                        <li>Violation of any rights of a third party.</li>
                    </ul>
                </section>

                <footer>
                    <h2>Contact Us</h2>
                    <address-block />
                    <p><strong>Last Updated:</strong> June 09, 2025</p>
                </footer>
            </div>
        </template>
    </docu-layout>
</template>

<script setup lang="ts">
import DocuLayout from '@/Layouts/DocuLayout.vue'
import { Ajility } from '@/Enums/Ajility'
import { useHead } from '@vueuse/head'
import { schemaTagOptions } from '@/Composables/SchemaTagOptions'
import AddressBlock from '@/Pages/AddressBlock.vue'
import { convertToGB } from '../Composables/convertToGB'
import { SubscriptionMeters } from '../Enums/SubscriptionPlan'

useHead(
    schemaTagOptions({
        title: 'Terms and Conditions | {{ Ajility.DBAName }}',
        description:
            'Read the terms and conditions for using {{ Ajility.DBAName }}, an online document storage and management service. Learn about your rights and responsibilities as a user.',
        schemaType: 'WebPage',
        robots: 'index, follow',
    }),
)
</script>

<style scoped>
.terms-container {
    max-width: 75%;
    margin: 0 auto;
    padding: 2rem;
    font-family: Arial, sans-serif;
    line-height: 1.6;
    background-color: #f9f9f9;
    border-radius: 8px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.terms-title {
    font-size: 2rem;
    font-weight: bold;
    text-align: center;
    margin-bottom: 1.5rem;
    color: #333;
}

.intro-text {
    font-size: 1.2rem;
    color: #555;
    margin-bottom: 1.5rem;
}

h2 {
    font-size: 1.5rem;
    color: #444;
    margin-top: 2rem;
}

ul {
    margin-left: 2rem;
    margin-bottom: 1.5rem;
}

ul li {
    margin-bottom: 0.5rem;
    list-style-type: square;
}

.link {
    color: #007bff;
    text-decoration: none;
}

.link:hover {
    text-decoration: underline;
}

footer {
    margin-top: 2rem;
    text-align: center;
    font-size: 0.9rem;
    color: #666;
}

address {
    font-style: normal;
    line-height: 1.5;
    margin-bottom: 1rem;
}
</style>
